import { useErrorInlineNotificationAPI } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import { useUpdateTransactionItem } from '@/context/services/transaction_items/Detail/UpdateTransactionItem.service';
import { ApInvoiceTransactionStatusEnum } from '@/context/services/transaction_items/List/TransactionItems.service';
import { TransactionItemDetailFormType } from '@/features/transaction_items/Detail/type';
import { extractVisibleCustomLimitErrorMessage } from '@/features/transaction_items/utils/extractVisibleCustomLimitErrorMessage';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useCallback } from 'react';
import { SubmitHandler, UseFormSetError } from 'react-hook-form';
import { useParams } from 'react-router-dom';

type Props = {
  setValidationError: UseFormSetError<TransactionItemDetailFormType>;
  handlerSuccess: (successMsg: string) => void;
  handlerError: (errorMsg: string) => void;
  showLoading: (loading: boolean) => void;
  onServerVisibleLimitError?: (message: string) => void;
};

export const useSubmitUpdatePullDownTransactionItem = ({
  setValidationError,
  handlerSuccess,
  handlerError,
  showLoading,
  onServerVisibleLimitError,
}: Props) => {
  const { transaction_item_id } = useParams();
  const { mutateAsync } = useUpdateTransactionItem(transaction_item_id ?? '');
  const { send } = useErrorTrackingSystem();
  const { t } = useTranslation();
  const setErrorNotification = useErrorInlineNotificationAPI();

  const onSubmitUpdate: SubmitHandler<TransactionItemDetailFormType> =
    useCallback(
      async (form) => {
        try {
          showLoading(true);
          const resp = await mutateAsync({
            id: transaction_item_id || '',
            data: {
              ap_invoice_transaction_general_input: {
                label: form.label,
                is_visible: form.is_visible,
                ...(form.is_required !== undefined
                  ? { is_required: form.is_required }
                  : {}),
                tooltip_message: form.tooltip_message,
              },
            },
          });
          if (resp.status === ApInvoiceTransactionStatusEnum.SUCCESS) {
            handlerSuccess(t('transaction_items.update.success'));
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.INVALID_ERROR
          ) {
            const limitMsg = extractVisibleCustomLimitErrorMessage(resp.errors);
            if (limitMsg) {
              if (onServerVisibleLimitError) {
                onServerVisibleLimitError(limitMsg);
                return;
              }
              handlerError(t('transaction_items.update.error'));
              return;
            }
            Object.entries(resp.errors.errors).forEach(([key, value]) => {
              if (key in form) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                setValidationError(key as keyof TransactionItemDetailFormType, {
                  type: 'validate',
                  message:
                    value.type === 'taken'
                      ? t('transaction_items.validation_message.taken')
                      : value.message,
                });
              } else {
                handlerError(t('transaction_items.update.error'));
              }
            });
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.AUTH_ERROR
          ) {
            location.href = resp.redirectPath;
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.PROXY_LOGIN_ERROR
          ) {
            const errorMessage =
              resp.errors.messages?.join('\n') || t('system_error');
            setErrorNotification(new Error(errorMessage));
          } else if (
            resp.status === ApInvoiceTransactionStatusEnum.CORPORATE_REQUIRED
          ) {
            setErrorNotification(new Error(resp.errors.messages?.join('\n')));
          } else {
            handlerError(t('transaction_items.update.error'));
          }
        } catch (error) {
          if (error instanceof Error && error?.name === 'AxiosError') {
            send(error.message);
          } else {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            send(error as Error);
          }
          handlerError(t('transaction_items.update.error'));
        } finally {
          showLoading(false);
        }
      },
      [
        mutateAsync,
        transaction_item_id,
        handlerSuccess,
        t,
        setValidationError,
        handlerError,
        send,
        showLoading,
        setErrorNotification,
        onServerVisibleLimitError,
      ]
    );

  return { onSubmitUpdate };
};
